// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A QR code, generated in code (M5-31).
//
// `/headset` shows a phone a URL to open on a Quest. That is the whole use: one short HTTPS URL,
// scanned once, by a phone camera, in good light. Byte mode, error-correction level M, versions 1
// to 10 — which covers a URL of up to 216 bytes and is far more than a signed-in machine URL
// needs.
//
// **Written rather than depended on**, for the reason every asset in this product is generated:
// there is no image file, no font, no runtime dependency and nothing to license. A QR symbol is a
// completely specified algorithm (ISO/IEC 18004) with no artistic content, which makes it exactly
// the kind of thing that is cheaper to write than to import. It is cross-checked against an
// independent implementation in the tests rather than trusted.
//
// **Level M and not L.** A code on a phone screen photographed by another phone loses modules to
// glare and moiré; M recovers 15 % against L's 7 % for one version step of size. A code that
// fails to scan is worse than a code that is slightly bigger, because the failure mode is a
// player concluding the feature is broken.
//
// **The scheme is not optional**, and the reason is in 08 §7: bare `host:port` payloads are not
// recognised as links by phone cameras, and HTTPS-Only Safari refuses `http://` ones. Both were
// learned on device. `qrPayloadProblem` refuses a payload without one rather than encoding a
// string that will scan perfectly and do nothing.

/** A finished symbol. `size` modules square, row-major, true where a module is dark. */
export interface QrSymbol {
  readonly size: number
  readonly modules: Uint8Array
  readonly version: number
  /** Which of the eight masks scored best. Recorded because it is the one non-obvious choice. */
  readonly mask: number
}

/** Modules of light margin around the symbol. Four is the standard's minimum. */
export const QUIET_ZONE = 4

/** Highest version this encoder emits. Version 10 at level M holds 216 bytes. */
export const MAX_VERSION = 10

/**
 * Data codewords available at level M, by version. Index 0 is version 1.
 *
 * Every row of the block table below sums to these, and a test asserts it — an encoder whose
 * capacity table and block table disagree produces a symbol that is structurally valid, scans,
 * and yields the wrong bytes.
 */
const DATA_CODEWORDS_M = [16, 28, 44, 64, 86, 108, 124, 154, 182, 216] as const

/** Total codewords by version, level-independent. */
const TOTAL_CODEWORDS = [26, 44, 70, 100, 134, 172, 196, 242, 292, 346] as const

/** EC codewords per block, and the block sizes, at level M. `[ecPerBlock, [count, dataLen]…]`. */
const BLOCKS_M: readonly (readonly [number, readonly (readonly [number, number])[]])[] = [
  [10, [[1, 16]]],
  [16, [[1, 28]]],
  [26, [[1, 44]]],
  [18, [[2, 32]]],
  [24, [[2, 43]]],
  [16, [[4, 27]]],
  [18, [[4, 31]]],
  [22, [[2, 38], [2, 39]]],
  [22, [[3, 36], [2, 37]]],
  [26, [[4, 43], [1, 44]]],
]

/** Alignment-pattern centre coordinates by version. */
const ALIGNMENT: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
]

/** Why a payload cannot be encoded, or undefined. */
export function qrPayloadProblem(payload: string): string | undefined {
  if (payload.length === 0) return 'there is nothing to encode'
  if (!/^https?:\/\//iu.test(payload)) {
    // 08 §7, learned on device twice: a bare host does not become a link, and HTTPS-Only Safari
    // refuses http. Refusing here is the difference between a bug and a support ticket.
    return 'a scannable payload needs a scheme — phone cameras do not linkify a bare host'
  }
  const bytes = new TextEncoder().encode(payload).length
  const capacity = DATA_CODEWORDS_M[MAX_VERSION - 1] as number
  // Mode nibble + 16-bit length at version 10 costs 20 bits, which is under three codewords.
  if (bytes + 3 > capacity) return `too long to encode: ${bytes} bytes, limit ${capacity - 3}`
  return undefined
}

/** Encode a payload. Throws only on a payload `qrPayloadProblem` would have refused. */
export function encodeQr(payload: string): QrSymbol {
  const problem = qrPayloadProblem(payload)
  if (problem !== undefined) throw new Error(`qr: ${problem}`)

  const bytes = new TextEncoder().encode(payload)
  const version = smallestVersion(bytes.length)
  const data = buildDataCodewords(bytes, version)
  const codewords = interleave(data, version)

  let best: QrSymbol | undefined
  let bestPenalty = Number.POSITIVE_INFINITY
  for (let mask = 0; mask < 8; mask++) {
    const symbol = draw(codewords, version, mask)
    const score = penalty(symbol)
    if (score < bestPenalty) {
      bestPenalty = score
      best = symbol
    }
  }
  // Eight masks are always tried, so this is always assigned.
  return best as QrSymbol
}

function smallestVersion(byteLength: number): number {
  for (let v = 1; v <= MAX_VERSION; v++) {
    const headerBits = 4 + (v >= 10 ? 16 : 8)
    const capacityBits = (DATA_CODEWORDS_M[v - 1] as number) * 8
    if (headerBits + byteLength * 8 <= capacityBits) return v
  }
  return MAX_VERSION
}

/** Mode indicator, length, payload, terminator, padding — §7.4 of the standard. */
function buildDataCodewords(bytes: Uint8Array, version: number): Uint8Array {
  const capacity = DATA_CODEWORDS_M[version - 1] as number
  const bits = new BitWriter()
  bits.write(0b0100, 4) // byte mode
  bits.write(bytes.length, version >= 10 ? 16 : 8)
  for (const b of bytes) bits.write(b, 8)

  // Terminator: up to four zero bits, but never past the end.
  const remaining = capacity * 8 - bits.length
  bits.write(0, Math.min(4, remaining))
  // Pad to a byte boundary, then alternate the two specified pad codewords.
  while (bits.length % 8 !== 0) bits.write(0, 1)
  const out = bits.bytes()
  const padded = new Uint8Array(capacity)
  padded.set(out.subarray(0, Math.min(out.length, capacity)))
  for (let i = out.length, alt = 0; i < capacity; i++, alt++) {
    padded[i] = alt % 2 === 0 ? 0xec : 0x11
  }
  return padded
}

/** Split into blocks, compute EC for each, and interleave both — §7.6. */
function interleave(data: Uint8Array, version: number): Uint8Array {
  const [ecPerBlock, groups] = BLOCKS_M[version - 1] as readonly [
    number,
    readonly (readonly [number, number])[],
  ]
  const dataBlocks: Uint8Array[] = []
  const ecBlocks: Uint8Array[] = []
  let at = 0
  for (const [count, length] of groups) {
    for (let i = 0; i < count; i++) {
      const block = data.subarray(at, at + length)
      at += length
      dataBlocks.push(block)
      ecBlocks.push(reedSolomon(block, ecPerBlock))
    }
  }

  const out = new Uint8Array(TOTAL_CODEWORDS[version - 1] as number)
  let o = 0
  const longest = Math.max(...dataBlocks.map((b) => b.length))
  for (let i = 0; i < longest; i++) {
    for (const block of dataBlocks) if (i < block.length) out[o++] = block[i] as number
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) out[o++] = block[i] as number
  }
  return out
}

// --- GF(256) -----------------------------------------------------------------------------
//
// The field the standard specifies: primitive polynomial x⁸ + x⁴ + x³ + x² + 1 (0x11d), generator
// α = 2. Tables built once at module load — 512 bytes, and it removes a multiply-and-reduce from
// the inner loop of every block.

const EXP = new Uint8Array(512)
const LOG = new Uint8Array(256)
{
  let x = 1
  for (let i = 0; i < 255; i++) {
    EXP[i] = x
    LOG[x] = i
    x <<= 1
    if (x & 0x100) x ^= 0x11d
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255] as number
}

function mul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return EXP[((LOG[a] as number) + (LOG[b] as number)) % 255] as number
}

/** The generator polynomial for `degree` EC codewords: ∏(x − α^i). */
function generator(degree: number): Uint8Array {
  let poly = new Uint8Array([1])
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1)
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j] as number) ^ (poly[j] as number)
      next[j + 1] = (next[j + 1] as number) ^ mul(poly[j] as number, EXP[i] as number)
    }
    poly = next
  }
  return poly
}

/** Polynomial long division; the remainder is the EC block. */
function reedSolomon(data: Uint8Array, ecLength: number): Uint8Array {
  const gen = generator(ecLength)
  const buffer = new Uint8Array(data.length + ecLength)
  buffer.set(data)
  for (let i = 0; i < data.length; i++) {
    const factor = buffer[i] as number
    if (factor === 0) continue
    for (let j = 0; j < gen.length; j++) {
      buffer[i + j] = (buffer[i + j] as number) ^ mul(gen[j] as number, factor)
    }
  }
  return buffer.subarray(data.length)
}

// --- the matrix --------------------------------------------------------------------------

class BitWriter {
  private readonly out: number[] = []
  private current = 0
  private used = 0
  length = 0

  write(value: number, bits: number): void {
    for (let i = bits - 1; i >= 0; i--) {
      this.current = (this.current << 1) | ((value >> i) & 1)
      this.used++
      this.length++
      if (this.used === 8) {
        this.out.push(this.current)
        this.current = 0
        this.used = 0
      }
    }
  }

  bytes(): Uint8Array {
    if (this.used > 0) {
      this.out.push(this.current << (8 - this.used))
      this.current = 0
      this.used = 0
    }
    return Uint8Array.from(this.out)
  }
}

/** Modules that carry structure rather than data, so the zigzag skips them. */
function reserved(size: number, version: number): Uint8Array {
  const map = new Uint8Array(size * size)
  const mark = (x: number, y: number): void => {
    if (x >= 0 && y >= 0 && x < size && y < size) map[y * size + x] = 1
  }
  // Finders and their separators, and the format-info strips beside them.
  for (const [ox, oy] of [
    [0, 0],
    [size - 7, 0],
    [0, size - 7],
  ] as const) {
    for (let y = -1; y <= 7; y++) for (let x = -1; x <= 7; x++) mark(ox + x, oy + y)
  }
  for (let i = 0; i < size; i++) {
    mark(6, i)
    mark(i, 6)
  }
  for (const cx of ALIGNMENT[version - 1] as readonly number[]) {
    for (const cy of ALIGNMENT[version - 1] as readonly number[]) {
      // The three corners already hold finders.
      if ((cx === 6 && cy === 6) || (cx === 6 && cy === size - 7) || (cx === size - 7 && cy === 6)) {
        continue
      }
      for (let y = -2; y <= 2; y++) for (let x = -2; x <= 2; x++) mark(cx + x, cy + y)
    }
  }
  // Format information, both copies.
  for (let i = 0; i < 9; i++) {
    mark(i, 8)
    mark(8, i)
  }
  for (let i = 0; i < 8; i++) {
    mark(size - 1 - i, 8)
    mark(8, size - 1 - i)
  }
  // Version information, versions 7 and up.
  if (version >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        mark(size - 11 + j, i)
        mark(i, size - 11 + j)
      }
    }
  }
  return map
}

function draw(codewords: Uint8Array, version: number, mask: number): QrSymbol {
  const size = version * 4 + 17
  const modules = new Uint8Array(size * size)
  const fixed = reserved(size, version)
  const set = (x: number, y: number, dark: boolean): void => {
    modules[y * size + x] = dark ? 1 : 0
  }

  // Finder patterns.
  for (const [ox, oy] of [
    [0, 0],
    [size - 7, 0],
    [0, size - 7],
  ] as const) {
    for (let y = 0; y < 7; y++) {
      for (let x = 0; x < 7; x++) {
        const edge = x === 0 || x === 6 || y === 0 || y === 6
        const core = x >= 2 && x <= 4 && y >= 2 && y <= 4
        set(ox + x, oy + y, edge || core)
      }
    }
  }
  // Timing patterns.
  for (let i = 8; i < size - 8; i++) {
    set(i, 6, i % 2 === 0)
    set(6, i, i % 2 === 0)
  }
  // The dark module, which is always dark and is the standard's, not a decoration.
  set(8, size - 8, true)

  // Alignment patterns.
  const centres = ALIGNMENT[version - 1] as readonly number[]
  for (const cx of centres) {
    for (const cy of centres) {
      if ((cx === 6 && cy === 6) || (cx === 6 && cy === size - 7) || (cx === size - 7 && cy === 6)) {
        continue
      }
      for (let y = -2; y <= 2; y++) {
        for (let x = -2; x <= 2; x++) {
          const ring = Math.max(Math.abs(x), Math.abs(y))
          set(cx + x, cy + y, ring !== 1)
        }
      }
    }
  }

  // Data, in the standard's two-module-wide upward-then-downward zigzag.
  let bit = 0
  let upward = true
  for (let right = size - 1; right > 0; right -= 2) {
    // Column 6 is the vertical timing pattern and is skipped entirely, not merely reserved.
    if (right === 6) right--
    for (let step = 0; step < size; step++) {
      const y = upward ? size - 1 - step : step
      for (let c = 0; c < 2; c++) {
        const x = right - c
        if (fixed[y * size + x] === 1) continue
        const byte = codewords[bit >> 3] ?? 0
        const value = ((byte >> (7 - (bit & 7))) & 1) === 1
        bit++
        set(x, y, value !== maskAt(mask, x, y))
      }
    }
    upward = !upward
  }

  writeFormat(modules, size, mask)
  if (version >= 7) writeVersion(modules, size, version)
  return { size, modules, version, mask }
}

/** The eight mask conditions, §7.8.2. True means the module is inverted. */
function maskAt(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0
    case 1:
      return y % 2 === 0
    case 2:
      return x % 3 === 0
    case 3:
      return (x + y) % 3 === 0
    case 4:
      return (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0
    default:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
  }
}

/** Level M is `00`; the 15-bit BCH code and its 0x5412 mask are the standard's. */
export function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask
  let bch = data << 10
  for (let i = 14; i >= 10; i--) {
    if (((bch >> i) & 1) === 1) bch ^= 0b10100110111 << (i - 10)
  }
  return ((data << 10) | bch) ^ 0b101010000010010
}

function writeFormat(modules: Uint8Array, size: number, mask: number): void {
  const bits = formatBits(mask)
  const put = (x: number, y: number, on: boolean): void => {
    modules[y * size + x] = on ? 1 : 0
  }
  for (let i = 0; i < 15; i++) {
    const on = ((bits >> i) & 1) === 1
    // First copy, around the top-left finder.
    if (i < 6) put(8, i, on)
    else if (i === 6) put(8, 7, on)
    else if (i === 7) put(8, 8, on)
    else if (i === 8) put(7, 8, on)
    else put(14 - i, 8, on)
    // Second copy, split between the other two finders.
    if (i < 8) put(size - 1 - i, 8, on)
    else put(8, size - 15 + i, on)
  }
}

/** The 18-bit version code, versions 7 and up. */
export function versionBits(version: number): number {
  let bch = version << 12
  for (let i = 17; i >= 12; i--) {
    // The BCH(18,6) generator: x¹² + x¹¹ + x¹⁰ + x⁹ + x⁸ + x⁵ + x² + 1.
    if (((bch >> i) & 1) === 1) bch ^= 0b1111100100101 << (i - 12)
  }
  return (version << 12) | bch
}

function writeVersion(modules: Uint8Array, size: number, version: number): void {
  const bits = versionBits(version)
  for (let i = 0; i < 18; i++) {
    const on = ((bits >> i) & 1) === 1
    const a = Math.floor(i / 3)
    const b = (i % 3) + size - 11
    modules[b * size + a] = on ? 1 : 0
    modules[a * size + b] = on ? 1 : 0
  }
}

// --- mask selection ------------------------------------------------------------------------

/** The four penalty rules of §7.8.3.1. Lower is better. */
function penalty(symbol: QrSymbol): number {
  const { size, modules } = symbol
  const at = (x: number, y: number): number => modules[y * size + x] as number
  let score = 0

  // Rule 1: runs of five or more of the same colour, in both directions.
  for (let i = 0; i < size; i++) {
    for (const horizontal of [true, false]) {
      let run = 1
      let previous = -1
      for (let j = 0; j < size; j++) {
        const value = horizontal ? at(j, i) : at(i, j)
        if (value === previous) {
          run++
          if (run === 5) score += 3
          else if (run > 5) score += 1
        } else {
          run = 1
          previous = value
        }
      }
    }
  }

  // Rule 2: every 2 × 2 block of one colour.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const v = at(x, y)
      if (v === at(x + 1, y) && v === at(x, y + 1) && v === at(x + 1, y + 1)) score += 3
    }
  }

  // Rule 3: the finder-like 1:1:3:1:1 pattern with four light modules on either side.
  //
  // **The four light modules must be inside the symbol.** The standard describes the pattern as
  // "preceded or followed by light area 4 modules wide" and does not say whether the quiet zone
  // counts, which is a real ambiguity that implementations split on. Treating out-of-bounds as
  // light scores every finder-adjacent run twice and chose a different mask from the reference
  // implementation on a third of the URLs tried. Requiring the light area to be *in* the symbol
  // agrees with it on all of them, and a mask both ends agree on is worth more than a reading of
  // one sentence.
  const pattern = [1, 0, 1, 1, 1, 0, 1]
  const light = [0, 0, 0, 0]
  const matches = (
    read: (k: number) => number | undefined,
    start: number,
    seq: number[],
  ): boolean => {
    for (let k = 0; k < seq.length; k++) if (read(start + k) !== seq[k]) return false
    return true
  }
  for (let i = 0; i < size; i++) {
    for (const horizontal of [true, false]) {
      const read = (k: number): number | undefined =>
        k < 0 || k >= size ? undefined : horizontal ? at(k, i) : at(i, k)
      for (let j = 0; j <= size - 7; j++) {
        if (!matches(read, j, pattern)) continue
        if (matches(read, j + 7, light) || matches(read, j - 4, light)) score += 40
      }
    }
  }

  // Rule 4: deviation from an even split of dark and light.
  let dark = 0
  for (const m of modules) dark += m
  const percent = (dark * 100) / (size * size)
  score += Math.floor(Math.abs(percent - 50) / 5) * 10
  return score
}

/**
 * The symbol as an SVG path, one `d` string of unit squares.
 *
 * A path rather than a rect per module: a version-10 symbol is 57 × 57 and would be up to three
 * thousand elements, which is a slow layout and a large DOM for something that never changes. One
 * path draws in one operation and scales with the viewport for free.
 */
export function qrPath(symbol: QrSymbol, quiet = QUIET_ZONE): string {
  const parts: string[] = []
  for (let y = 0; y < symbol.size; y++) {
    for (let x = 0; x < symbol.size; x++) {
      if (symbol.modules[y * symbol.size + x] === 1) {
        parts.push(`M${x + quiet} ${y + quiet}h1v1h-1z`)
      }
    }
  }
  return parts.join('')
}

/** The `viewBox` that goes with `qrPath`. */
export function qrViewBox(symbol: QrSymbol, quiet = QUIET_ZONE): string {
  const side = symbol.size + quiet * 2
  return `0 0 ${side} ${side}`
}
